<script setup lang="tsx">
import { CirclePlus, Fold } from "@element-plus/icons-vue";
import { Column, ElButton, SortBy, TableV2SortOrder } from "element-plus";
import { computed, onMounted, ref } from "vue";
import TreeDefinitionEditor from "~/Compliance/components/TreeDefinitionEditor.vue";
import ComplianceQuery from "~/Compliance/components/ComplianceQuery.vue";
import TableWrapper from "~/components/TableWrapper.vue";
import { useTreeDefinitionStore } from "~/stores/TreeDefinitionStore";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { useStableAutoResizer } from "~/utils/resizeObserverFix.simple";
import type { TreeDefinitionSummaryDto } from "~/api";

const treeDefinitionStore = useTreeDefinitionStore();
const myInfoStore = useMyInfoStore();
const loading = ref(true);
const items = ref<TreeDefinitionSummaryDto[]>([]);
const selectedRow = ref<TreeDefinitionSummaryDto | undefined>(undefined);
const drawerOpen = ref(false);

const { autoResizerRef } = useStableAutoResizer();

onMounted(async () => {
  await treeDefinitionStore.load();
  items.value = [...treeDefinitionStore.treeDefinitions];
  loading.value = false;
  sortResults();
});

const sortState = ref<SortBy>({ key: 0, order: TableV2SortOrder.ASC });
const onSort = (params: SortBy) => {
  sortState.value = params;
  sortResults();
};

const sortResults = () => {
  const { key, order } = sortState.value;
  if (key) {
    const delta = order === TableV2SortOrder.ASC ? 1 : -1;
    items.value.sort((a, b) => {
      return (a[key as keyof TreeDefinitionSummaryDto] ?? "") >
        (b[key as keyof TreeDefinitionSummaryDto] ?? "")
        ? delta
        : -delta;
    });
  } else {
    items.value.sort((a, b) => a.name.localeCompare(b.name));
  }
};

let numCreated = 0;
const createItem = () => {
  selectedRow.value = {
    guid: "new" + numCreated++,
  } as TreeDefinitionSummaryDto;
  drawerOpen.value = true;
};

const showItem = (row: TreeDefinitionSummaryDto) => {
  selectedRow.value = row;
  drawerOpen.value = true;
};

const refreshList = async () => {
  loading.value = true;
  await treeDefinitionStore.load(true);
  items.value = [...treeDefinitionStore.treeDefinitions];
  loading.value = false;
  sortResults();
};

const closeDrawer = () => {
  drawerOpen.value = false;
  selectedRow.value = undefined;
};

const handleAdded = () => {
  refreshList();
};

const handleUpdated = () => {
  refreshList();
};

const handleDeleted = () => {
  refreshList();
};

const complianceQueryInput = ref<{
  type: string[];
  importance: string[];
  status: string[];
  search: string;
}>({
  type: [],
  importance: [],
  status: [],
  search: "",
});

const itemsFiltered = computed(() => {
  const searchFiltered = complianceQueryInput.value.search !== "";

  return items.value.filter((item) => {
    if (searchFiltered) {
      if (
        !item.name?.toLowerCase().includes(complianceQueryInput.value.search)
      ) {
        return false;
      }
    }
    return true;
  });
});

const columns: Column<any>[] = [
  {
    title: "Name",
    key: "name",
    dataKey: "name",
    width: 300,
    sortable: true,
    cellRenderer: (row: any) => (
      <ElButton
        text
        onClick={() => showItem(row.rowData)}
        icon={Fold}
        type="primary"
        style="font-size: 15px;"
      >
        {row.rowData.name}
      </ElButton>
    ),
  },
  {
    title: "Type",
    key: "treeType",
    dataKey: "treeType",
    width: 200,
    sortable: true,
    cellRenderer: (row: any) => row.rowData.treeType ?? "General",
  },
  {
    title: "Description",
    key: "description",
    dataKey: "description",
    width: 600,
    sortable: true,
  },
  {
    title: "Levels",
    key: "levelCount",
    dataKey: "levelCount",
    width: 100,
    sortable: true,
  },
  {
    title: "Nodes",
    key: "nodeCount",
    dataKey: "nodeCount",
    width: 100,
    sortable: true,
  },
];
</script>

<template>
  <section>
    <div class="flexRow">
      <span class="showingInTable">
        Showing {{ itemsFiltered.length }} of {{ items.length }}
      </span>
      <RefreshButton @click="refreshList" />
      <ComplianceQuery @submit:query="complianceQueryInput = $event">
        <template #create-button> </template>
      </ComplianceQuery>

      <el-button
        v-if="myInfoStore.hasSetupBasicRole"
        class="addButton"
        :icon="CirclePlus"
        title="Create a new tree definition"
        @click="createItem"
      >
        New Tree Definition
      </el-button>
    </div>
  </section>

  <ElAutoResizer ref="autoResizerRef" v-loading="loading">
    <template #default="{ height, width }">
      <TableWrapper
        v-if="!loading"
        :columns="columns"
        :data="itemsFiltered"
        row-key="guid"
        :height="height"
        :width="width"
        :sort-state="sortState"
        @column-sort="onSort"
      />
    </template>
  </ElAutoResizer>

  <TreeDefinitionEditor
    v-if="selectedRow?.guid"
    :key="selectedRow.guid"
    :tree-definition-summary="selectedRow"
    @close-drawer="closeDrawer"
    @added="handleAdded"
    @updated="handleUpdated"
    @deleted="handleDeleted"
  />
</template>
